/**
 * @module app.um
 */

import { autoinject, observable } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { AuthChangeEvent, AuthAutoDeterminationEvent, AuthReloadEvent } from 'app/um/lib/events';
import { persist } from 'app/shared/lib/persist-decorator';
import { authIsReadOnly } from 'app/um/lib/utilities';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import { openAlternateAuthSummary } from 'app/um/components/auth/alternate-auth-summary';
import { openContactLog } from 'app/cd/components/contact-log/contact-log';
import { openAuthAuditLog as openAuditLog } from 'app/um/components/audit-log/auth-audit-log';
import { openClaimSystemLog } from 'app/um/components/claim-system-log/claim-system-log';
import { IContactLogConfig } from 'app/core/models/essette-interfaces';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { SaveAuth } from 'app/um/components/auth/save-auth';
import * as UM from 'app/um/models/essette-api';

interface IAuthNavAccess {
    auditLog: boolean;
    claimSystemLog: boolean;
    contactLog: boolean;
}

@autoinject()
export class AuthRibbon extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    @observable
    protected auth: UM.Auth = {};
    protected isValid: boolean = false;
    protected validate = validate;

    @persist({ initialValue: false })
    protected ribbonExpanded!: boolean;
    private subscriptions: Array<Subscription> = [];

    protected canEditAuthDetails: boolean = false;
    protected authNavAccess: IAuthNavAccess | null = null;
    protected showAuthSummaryLink: boolean = false;
    protected showAuthHistoryCode: boolean = false;
    protected hasAuthRoute = false;
    protected openAuthSummary = openAuthSummary;
    protected openAlternateAuthSummary = openAlternateAuthSummary;
    protected openContactLog = openContactLog;
    protected openAuditLog = openAuditLog;
    protected openClaimSystemLog = openClaimSystemLog;

    protected contactLogConfig: IContactLogConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        moduleCode: 'UM',
        rollupConfig: {
            serviceRootUrl: 'cd/members',
            targetType: 'CD.Member'
        }
    };

    ////****************** Element References ***************************************////
    public $modal!: IPopup;
    protected $saveAuth!: SaveAuth;

    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly context: AuthPageContext,
        private readonly events: EventAggregator,
        protected env: EnvironmentService,
        ...rest: any[]) {

        super(...rest);
    }

    protected async activate(): Promise<void> {
        await this.reload();

        this.subscriptions.push(
            this.events.subscribe(AuthChangeEvent, (e: AuthChangeEvent) => {
                this.reload();
            }),
            this.events.subscribe(AuthAutoDeterminationEvent, (e: AuthAutoDeterminationEvent) => {
                this.reload();
            }),
            this.events.subscribe(AuthReloadEvent, (e: AuthReloadEvent) => {
                this.reload();
            })
        );
    }

    protected detached(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }

    ////****************** Events ***********************************************////
    protected async authChanged(): Promise<void> {
        if(!this.auth) return;
        await this.checkPermissions();
    }

    protected async formSuccess(): Promise<void> {
        this.$modal.close();
        await this.checkPermissions();
    }

    public async reload(): Promise<void> {
        this.auth = this.context.auth;
        await this.checkPermissions();
    }

    ////****************** Functions ***********************************************////
    protected editAuth(): void {
        this.$modal.open();
        this.$saveAuth.configureAuth();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        const hasEditAuthDetails = this.accountContext.hasPermission('UM.Auth.Update.EditAuthDetails');
        const hasEditAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditAuth');
        const hasEditClosedAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditClosedAuth');
        const readonly = this.auth ? authIsReadOnly(this.auth, this.env.user.id, hasEditAuth, hasEditClosedAuth) : true;
        this.canEditAuthDetails = !readonly && hasEditAuthDetails;
        this.showAuthSummaryLink = this.accountContext.hasRouteAccess('um.auth.summary');
        this.showAuthHistoryCode = this.accountContext.hasPermission('UM.AuthServiceDateHistory.Duty.ViewServiceDateCode');
        this.authNavAccess = {
            contactLog: this.accountContext.hasRouteAccess('um.auth.contact-log'),
            auditLog: this.accountContext.hasRouteAccess('um.auth.audit-log'),
            claimSystemLog: this.accountContext.hasRouteAccess('um.auth.claim-system-log') && this.context.syncToClaimsSystem
        } as IAuthNavAccess;
        this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
    }

    protected toggleExpand(): void {
        this.ribbonExpanded = !this.ribbonExpanded;
    }

    protected async openAuthContactLog(): Promise<void> {
        if(!this.auth.id || !this.auth.member) return;
        this.openContactLog(this.auth.member, this.contactLogConfig, this.auth.id);
    }

    protected async openAuthAuditLog(): Promise<void> {
        this.openAuditLog(this.auth);
    }

    protected async openAuthClaimSystemLog(): Promise<void> {
        this.openClaimSystemLog(this.auth);
    }

    ////****************** Service Calls ********************************************////


}
